// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the whole ClickUp workspace for the Tasks page — every space, folder
// and list (with open/overdue counts from the server's task index), paged task
// views per location, list statuses/members, space overviews, favourites and
// recent lists. Live: the server pushes KONVO_CLICKUP_TASKS_CHANGED with ids
// and the loaded views that contain them refetch.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Activity from '@app/features/konvo_activity/state/Activity';
import KonvoTasks, {
	groupKeyForTask,
	type KonvoFieldDefinition,
	type KonvoTask,
	type KonvoTaskListStatus,
	type KonvoTaskStatus,
	type KonvoTaskUser,
} from '@app/features/konvo_tasks/state/KonvoTasks';
import {sameLocation, type WorkspaceLocation, workspaceKey} from '@app/features/konvo_tasks/state/WorkspaceLocation';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {ConcurrencyGate} from '@app/features/platform/transport/ConcurrencyGate';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('KonvoWorkspace');

export interface WorkspaceListNode {
	id: string;
	name: string;
	space_id: string;
	folder_id: string | null;
	open_count: number;
	overdue_count: number;
	mine_count: number;
}
export interface WorkspaceFolderNode {
	id: string;
	name: string;
	open_count: number;
	overdue_count: number;
	mine_count: number;
	lists: Array<WorkspaceListNode>;
}
export interface WorkspaceSpaceNode {
	id: string;
	name: string;
	color: string | null;
	avatar: string | null;
	private: boolean;
	open_count: number;
	overdue_count: number;
	mine_count: number;
	folders: Array<WorkspaceFolderNode>;
	lists: Array<WorkspaceListNode>;
}
export interface WorkspaceHierarchy {
	connected: boolean;
	clickup_user_id: string | null;
	own_token: boolean;
	synced_at: number | null;
	workspace: {id: string; name: string | null} | null;
	spaces: Array<WorkspaceSpaceNode>;
}

export interface WorkspaceListMeta {
	list: {
		id: string;
		name: string;
		space_id: string | null;
		space_name: string | null;
		folder_id: string | null;
		folder_name: string | null;
	};
	statuses: Array<KonvoTaskListStatus>;
	members: Array<KonvoTaskUser>;
	/** Custom fields the list's tasks can carry; absent from older servers. */
	fields?: Array<KonvoFieldDefinition>;
}

export interface WorkspaceStatusCount extends KonvoTaskStatus {
	count: number;
}
export interface WorkspaceOverview {
	space: {id: string; name: string; color: string | null; private: boolean};
	open: number;
	overdue: number;
	not_started: number;
	due_this_week: number;
	due_this_week_mine: number;
	closed_last_7_days: number;
	closed_prev_7_days: number;
	/** When the server's task index last caught up with ClickUp; syncing = never synced yet. */
	synced_at?: number | null;
	syncing?: boolean;
	lists: Array<{
		id: string;
		name: string;
		folder_id: string | null;
		folder_name: string | null;
		open: number;
		overdue: number;
		closed: number;
		statuses: Array<WorkspaceStatusCount>;
		people: Array<KonvoTaskUser>;
		next_due: number | null;
	}>;
	people: Array<{user: KonvoTaskUser; open: number; statuses: Array<WorkspaceStatusCount>}>;
	activity: Array<{
		task_id: string;
		task_name: string;
		list_id: string | null;
		list_name: string | null;
		user: KonvoTaskUser | null;
		kind: 'created' | 'status' | 'closed' | 'comment' | 'assignee' | 'due' | 'priority' | 'moved';
		status: KonvoTaskStatus | null;
		at: number;
	}>;
}

export interface WorkspaceTaskView {
	key: string;
	location: WorkspaceLocation;
	includeClosed: boolean;
	mine: boolean;
	tasks: Array<KonvoTask>;
	total: number;
	nextCursor: string | null;
	loading: boolean;
	loadingMore: boolean;
	fetched: boolean;
	syncing: boolean;
	error: string | null;
}

export interface WorkspaceCreateTask {
	listId: string;
	name: string;
	description?: string;
	status?: string;
	assigneeIds?: Array<number>;
	dueDateMs?: number | null;
	dueDateTime?: boolean;
	startDateMs?: number | null;
	priority?: number | null;
	tags?: Array<string>;
	timeEstimateMs?: number | null;
	parent?: string;
}

export interface TasksChangedEvent {
	task_ids?: Array<string>;
	list_ids?: Array<string>;
	space_ids?: Array<string>;
	hierarchy?: boolean;
}

/** A location in the tree, resolved against the hierarchy. */
export interface WorkspaceNodeInfo {
	kind: 'space' | 'folder' | 'list';
	id: string;
	name: string;
	space: WorkspaceSpaceNode;
	folder: WorkspaceFolderNode | null;
	list: WorkspaceListNode | null;
}

const FAVOURITES_KEY = 'konvo:tasks:favourites';
const RECENT_LISTS_KEY = 'konvo:tasks:recent-lists';
const EXPANDED_KEY = 'konvo:tasks:expanded';
const HIERARCHY_CACHE_KEY = 'konvo:tasks:hierarchy:v1';
const MAX_RECENT = 6;
const PAGE_SIZE = 200;
const HIERARCHY_STALE_MS = 60_000;
const VIEW_STALE_MS = 30_000;

function readJson<T>(key: string, fallback: T): T {
	try {
		const raw = getProtectedLocalStorage()?.getItem(key);
		return raw ? (JSON.parse(raw) as T) : fallback;
	} catch {
		return fallback;
	}
}
function writeJson(key: string, value: unknown): void {
	try {
		getProtectedLocalStorage()?.setItem(key, JSON.stringify(value));
	} catch {}
}

export function isOpenStatus(status: Pick<KonvoTaskStatus, 'type'>): boolean {
	return status.type !== 'closed' && status.type !== 'done';
}

class KonvoWorkspaceStore {
	hierarchy: WorkspaceHierarchy | null = readJson<WorkspaceHierarchy | null>(HIERARCHY_CACHE_KEY, null);
	hierarchyLoading = false;
	hierarchyError: string | null = null;
	private hierarchyFetchedAt = 0;
	favourites: Array<string> = readJson<Array<string>>(FAVOURITES_KEY, []);
	recentLists: Array<string> = readJson<Array<string>>(RECENT_LISTS_KEY, []);
	expanded: Set<string> = new Set(readJson<Array<string>>(EXPANDED_KEY, []));
	views = observable.map<string, WorkspaceTaskView>();
	listMeta = observable.map<string, WorkspaceListMeta>();
	overviews = observable.map<string, WorkspaceOverview>();
	overviewLoading = observable.set<string>();
	private viewFetchedAt = new Map<string, number>();
	private listMetaLoading = new Set<string>();
	/** The workspace asks for every list's statuses at once; four at a time reach the API. */
	private listMetaGate = new ConcurrencyGate(4);
	private pendingChange: TasksChangedEvent | null = null;
	private changeTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<
			this,
			'hierarchyFetchedAt' | 'viewFetchedAt' | 'listMetaLoading' | 'listMetaGate' | 'pendingChange' | 'changeTimer'
		>(
			this,
			{
				hierarchyFetchedAt: false,
				viewFetchedAt: false,
				listMetaLoading: false,
				listMetaGate: false,
				pendingChange: false,
				changeTimer: false,
				expanded: observable,
			},
			{autoBind: true},
		);
		KonvoTasks.subscribeTaskChanges((task) => this.applyTask(task));
	}

	// ── Hierarchy ──────────────────────────────────────────────────────────

	get connected(): boolean | null {
		return this.hierarchy ? this.hierarchy.connected : null;
	}

	get spaces(): Array<WorkspaceSpaceNode> {
		return this.hierarchy?.spaces ?? [];
	}

	get clickupUserId(): string | null {
		return this.hierarchy?.clickup_user_id ?? KonvoTasks.clickupUserId;
	}

	/** Every list, with the space and folder it sits in. */
	get allLists(): Array<{list: WorkspaceListNode; space: WorkspaceSpaceNode; folder: WorkspaceFolderNode | null}> {
		const out: Array<{list: WorkspaceListNode; space: WorkspaceSpaceNode; folder: WorkspaceFolderNode | null}> = [];
		for (const space of this.spaces) {
			for (const list of space.lists) out.push({list, space, folder: null});
			for (const folder of space.folders) for (const list of folder.lists) out.push({list, space, folder});
		}
		return out;
	}

	get mineCount(): number {
		return this.spaces.reduce((sum, space) => sum + space.mine_count, 0);
	}

	get openCount(): number {
		return this.spaces.reduce((sum, space) => sum + space.open_count, 0);
	}

	get overdueMineCount(): number {
		// The hierarchy only knows overdue per container, not overdue-and-mine; the
		// "Assigned to me" row uses KonvoTasks for that (it holds my open tasks).
		return KonvoTasks.myTasks.filter(
			(task) => !task.parent && isOpenStatus(task.status) && groupKeyForTask(task) === 'overdue',
		).length;
	}

	findNode(kind: 'space' | 'folder' | 'list', id: string): WorkspaceNodeInfo | null {
		for (const space of this.spaces) {
			if (kind === 'space' && space.id === id) return {kind, id, name: space.name, space, folder: null, list: null};
			for (const list of space.lists) {
				if (kind === 'list' && list.id === id) return {kind, id, name: list.name, space, folder: null, list};
			}
			for (const folder of space.folders) {
				if (kind === 'folder' && folder.id === id) return {kind, id, name: folder.name, space, folder, list: null};
				for (const list of folder.lists) {
					if (kind === 'list' && list.id === id) return {kind, id, name: list.name, space, folder, list};
				}
			}
		}
		return null;
	}

	nodeFor(location: WorkspaceLocation): WorkspaceNodeInfo | null {
		if (!location.id || (location.kind !== 'space' && location.kind !== 'folder' && location.kind !== 'list'))
			return null;
		return this.findNode(location.kind, location.id);
	}

	async loadHierarchy(force = false): Promise<void> {
		if (this.hierarchyLoading) return;
		if (!force && this.hierarchyFetchedAt && Date.now() - this.hierarchyFetchedAt < HIERARCHY_STALE_MS) return;
		this.hierarchyLoading = true;
		try {
			const response = await http.get<WorkspaceHierarchy>(Endpoints.KONVO_CLICKUP_HIERARCHY, RETRY_RATE_LIMITS);
			const body = response.body;
			runInAction(() => {
				if (body) {
					this.hierarchy = body;
					writeJson(HIERARCHY_CACHE_KEY, body);
				}
				this.hierarchyError = null;
				this.hierarchyFetchedAt = Date.now();
			});
		} catch (error) {
			logger.warn('Failed to load the ClickUp hierarchy', error);
			runInAction(() => {
				this.hierarchyError = 'load_failed';
			});
		} finally {
			runInAction(() => {
				this.hierarchyLoading = false;
			});
		}
	}

	// ── Favourites, recents, expanded tree nodes ───────────────────────────

	/** Favourites are keyed like "list:123" / "folder:1" / "space:9". */
	isFavourite(kind: 'space' | 'folder' | 'list', id: string): boolean {
		return this.favourites.includes(`${kind}:${id}`);
	}

	toggleFavourite(kind: 'space' | 'folder' | 'list', id: string): void {
		const key = `${kind}:${id}`;
		this.favourites = this.favourites.includes(key)
			? this.favourites.filter((entry) => entry !== key)
			: [...this.favourites, key];
		writeJson(FAVOURITES_KEY, this.favourites);
	}

	/** Favourites that still exist, resolved against the hierarchy. */
	get favouriteNodes(): Array<WorkspaceNodeInfo> {
		return this.favourites
			.map((key) => {
				const [kind, ...rest] = key.split(':');
				return this.findNode(kind as 'space' | 'folder' | 'list', rest.join(':'));
			})
			.filter((node): node is WorkspaceNodeInfo => node != null);
	}

	noteRecentList(listId: string): void {
		this.recentLists = [listId, ...this.recentLists.filter((id) => id !== listId)].slice(0, MAX_RECENT);
		writeJson(RECENT_LISTS_KEY, this.recentLists);
	}

	get recentListNodes(): Array<WorkspaceNodeInfo> {
		return this.recentLists
			.map((id) => this.findNode('list', id))
			.filter((node): node is WorkspaceNodeInfo => node != null);
	}

	isExpanded(key: string): boolean {
		return this.expanded.has(key);
	}

	setExpanded(key: string, open: boolean): void {
		if (open) this.expanded.add(key);
		else this.expanded.delete(key);
		writeJson(EXPANDED_KEY, [...this.expanded]);
	}

	toggleExpanded(key: string): void {
		this.setExpanded(key, !this.expanded.has(key));
	}

	/** Open the tree down to a location so the selected row is visible. */
	reveal(location: WorkspaceLocation): void {
		const node = this.nodeFor(location);
		if (!node) return;
		this.setExpanded(`space:${node.space.id}`, true);
		if (node.folder) this.setExpanded(`folder:${node.folder.id}`, true);
	}

	// ── Task views ─────────────────────────────────────────────────────────

	getView(
		location: WorkspaceLocation,
		filters: {includeClosed?: boolean; mine?: boolean} = {},
	): WorkspaceTaskView | undefined {
		return this.views.get(workspaceKey(location, filters));
	}

	private queryFor(view: WorkspaceTaskView, cursor: string | null): Record<string, string> {
		const {location} = view;
		const query: Record<string, string> = {limit: String(PAGE_SIZE)};
		if (location.kind === 'space' || location.kind === 'folder' || location.kind === 'list') {
			query.scope = location.kind;
			query.id = location.id ?? '';
		} else {
			query.scope = 'all';
		}
		if (location.kind === 'mine' || location.kind === 'today' || location.kind === 'home' || view.mine) {
			query.assignee = 'me';
		}
		if (location.kind === 'today') query.due = 'today_overdue';
		if (view.includeClosed) query.include_closed = 'true';
		if (cursor) query.cursor = cursor;
		return query;
	}

	/** Loads (or refreshes) the first page of a location's tasks. */
	async loadView(
		location: WorkspaceLocation,
		filters: {includeClosed?: boolean; mine?: boolean} = {},
		force = false,
	): Promise<WorkspaceTaskView> {
		const key = workspaceKey(location, filters);
		let view = this.views.get(key);
		if (!view) {
			view = {
				key,
				location,
				includeClosed: Boolean(filters.includeClosed),
				mine: Boolean(filters.mine),
				tasks: [],
				total: 0,
				nextCursor: null,
				loading: false,
				loadingMore: false,
				fetched: false,
				syncing: false,
				error: null,
			};
			this.views.set(key, view);
			view = this.views.get(key)!;
		}
		const fetchedAt = this.viewFetchedAt.get(key) ?? 0;
		if (!force && view.fetched && Date.now() - fetchedAt < VIEW_STALE_MS) return view;
		if (view.loading) return view;
		view.loading = true;
		try {
			const response = await http.get<{
				connected: boolean;
				syncing?: boolean;
				tasks: Array<KonvoTask>;
				next_cursor: string | null;
				total: number;
				error?: string;
			}>(Endpoints.KONVO_CLICKUP_WORKSPACE_TASKS, {query: this.queryFor(view, null), ...RETRY_RATE_LIMITS});
			const body = response.body;
			runInAction(() => {
				const current = this.views.get(key);
				if (!current) return;
				current.tasks = body?.tasks ?? [];
				current.total = body?.total ?? current.tasks.length;
				current.nextCursor = body?.next_cursor ?? null;
				current.syncing = body?.syncing === true;
				current.error = body?.error ?? (body?.connected === false ? 'not_connected' : null);
				current.fetched = true;
				this.viewFetchedAt.set(key, Date.now());
			});
		} catch (error) {
			logger.warn('Failed to load workspace tasks', error);
			runInAction(() => {
				const current = this.views.get(key);
				if (current) {
					current.error = 'load_failed';
					current.fetched = true;
				}
			});
		} finally {
			runInAction(() => {
				const current = this.views.get(key);
				if (current) current.loading = false;
			});
		}
		return this.views.get(key)!;
	}

	async loadMore(location: WorkspaceLocation, filters: {includeClosed?: boolean; mine?: boolean} = {}): Promise<void> {
		const view = this.getView(location, filters);
		if (!view?.nextCursor || view.loadingMore) return;
		view.loadingMore = true;
		try {
			const response = await http.get<{tasks: Array<KonvoTask>; next_cursor: string | null; total: number}>(
				Endpoints.KONVO_CLICKUP_WORKSPACE_TASKS,
				{query: this.queryFor(view, view.nextCursor)},
			);
			runInAction(() => {
				const seen = new Set(view.tasks.map((task) => task.id));
				view.tasks = [...view.tasks, ...(response.body?.tasks ?? []).filter((task) => !seen.has(task.id))];
				view.nextCursor = response.body?.next_cursor ?? null;
				view.total = response.body?.total ?? view.total;
			});
		} catch (error) {
			logger.warn('Failed to load more workspace tasks', error);
		} finally {
			runInAction(() => {
				view.loadingMore = false;
			});
		}
	}

	/** Top-level tasks of a view (subtasks show under their parent, counted in subtask_total). */
	topLevel(view: WorkspaceTaskView | undefined): Array<KonvoTask> {
		if (!view) return [];
		const ids = new Set(view.tasks.map((task) => task.id));
		return view.tasks.filter((task) => !task.parent || !ids.has(task.parent));
	}

	subtasksOf(view: WorkspaceTaskView | undefined, parentId: string | null): Array<KonvoTask> {
		if (!view || !parentId) return [];
		return view.tasks.filter((task) => task.parent === parentId);
	}

	/** Whether a task belongs in a view (used when a task changes locally). */
	private taskFits(view: WorkspaceTaskView, task: KonvoTask): boolean {
		const {location} = view;
		if (!view.includeClosed && !isOpenStatus(task.status)) return false;
		const mineOnly = view.mine || location.kind === 'mine' || location.kind === 'today' || location.kind === 'home';
		if (mineOnly) {
			const me = this.clickupUserId;
			if (!me || !task.assignees.some((assignee) => String(assignee.id) === me)) return false;
		}
		if (location.kind === 'list') return task.list.id === location.id;
		if (location.kind === 'folder') return task.folder.id === location.id;
		if (location.kind === 'space') return task.space.id === location.id;
		return true;
	}

	/** Patch a changed task into every loaded view, adding or dropping it where it now (doesn't) fit. */
	applyTask(task: KonvoTask): void {
		if (!task.id) return;
		for (const view of this.views.values()) {
			const index = view.tasks.findIndex((entry) => entry.id === task.id);
			const fits = this.taskFits(view, task);
			if (index >= 0 && fits) view.tasks[index] = {...view.tasks[index], ...task};
			else if (index >= 0 && !fits) {
				view.tasks.splice(index, 1);
				view.total = Math.max(0, view.total - 1);
			} else if (index < 0 && fits && view.fetched) {
				view.tasks.push(task);
				view.total += 1;
			}
		}
	}

	removeTask(taskId: string): void {
		for (const view of this.views.values()) {
			const index = view.tasks.findIndex((entry) => entry.id === taskId);
			if (index >= 0) {
				view.tasks.splice(index, 1);
				view.total = Math.max(0, view.total - 1);
			}
		}
	}

	findTask(taskId: string): KonvoTask | null {
		for (const view of this.views.values()) {
			const task = view.tasks.find((entry) => entry.id === taskId);
			if (task) return task;
		}
		return KonvoTasks.getTask(taskId);
	}

	// ── List meta + overview ───────────────────────────────────────────────

	async loadListMeta(listId: string, force = false): Promise<WorkspaceListMeta | null> {
		if (!force && this.listMeta.has(listId)) return this.listMeta.get(listId)!;
		if (this.listMetaLoading.has(listId)) return this.listMeta.get(listId) ?? null;
		this.listMetaLoading.add(listId);
		try {
			const response = await this.listMetaGate.run(() =>
				http.get<WorkspaceListMeta>(Endpoints.KONVO_CLICKUP_LIST(listId), RETRY_RATE_LIMITS),
			);
			const body = response.body;
			if (body) {
				runInAction(() => {
					this.listMeta.set(listId, {
						...body,
						statuses: [...body.statuses].sort((a, b) => a.orderindex - b.orderindex),
					});
				});
			}
			return body ?? null;
		} catch (error) {
			logger.warn('Failed to load ClickUp list', error);
			return null;
		} finally {
			this.listMetaLoading.delete(listId);
		}
	}

	/** A custom field's definition (with every option) in a list, once its meta has loaded. */
	fieldDefinition(listId: string | null | undefined, fieldId: string): KonvoFieldDefinition | null {
		if (!listId) return null;
		return this.listMeta.get(listId)?.fields?.find((field) => field.id === fieldId) ?? null;
	}

	statusesFor(listId: string | null | undefined): Array<KonvoTaskListStatus> {
		return listId ? (this.listMeta.get(listId)?.statuses ?? []) : [];
	}

	/** The status after the task's current one in its list (for "next status" actions). */
	nextStatus(task: KonvoTask): KonvoTaskListStatus | null {
		const statuses = this.statusesFor(task.list.id);
		const index = statuses.findIndex(
			(status) => (status.status ?? '').toLowerCase() === (task.status.status ?? '').toLowerCase(),
		);
		if (index < 0) return null;
		const next = statuses[index + 1];
		return next && isOpenStatus(next) ? next : null;
	}

	async loadOverview(spaceId: string): Promise<WorkspaceOverview | null> {
		if (this.overviewLoading.has(spaceId)) return this.overviews.get(spaceId) ?? null;
		this.overviewLoading.add(spaceId);
		try {
			const response = await http.get<WorkspaceOverview>(Endpoints.KONVO_CLICKUP_SPACE_OVERVIEW(spaceId));
			const body = response.body;
			if (body) {
				runInAction(() => {
					this.overviews.set(spaceId, body);
				});
			}
			return body ?? null;
		} catch (error) {
			logger.warn('Failed to load the space overview', error);
			return null;
		} finally {
			runInAction(() => {
				this.overviewLoading.delete(spaceId);
			});
		}
	}

	// ── Mutations ──────────────────────────────────────────────────────────

	async createTask(input: WorkspaceCreateTask): Promise<KonvoTask | null> {
		const response = await http.post<{id: string; url: string; task?: KonvoTask}>(Endpoints.KONVO_CLICKUP_TASKS, {
			body: {
				list_id: input.listId,
				name: input.name,
				...(input.description ? {description: input.description} : {}),
				...(input.status ? {status: input.status} : {}),
				...(input.assigneeIds?.length ? {assignee_ids: input.assigneeIds} : {}),
				...(input.dueDateMs != null ? {due_date_ms: input.dueDateMs, due_date_time: input.dueDateTime === true} : {}),
				...(input.startDateMs != null ? {start_date_ms: input.startDateMs} : {}),
				...(input.priority != null ? {priority: input.priority} : {}),
				...(input.tags?.length ? {tags: input.tags} : {}),
				...(input.timeEstimateMs != null ? {time_estimate_ms: input.timeEstimateMs} : {}),
				...(input.parent ? {parent: input.parent} : {}),
			},
		});
		this.noteRecentList(input.listId);
		Activity.track('action.done', {action: 'task.create', origin: 'konvo'});
		const task = response.body?.task ?? null;
		if (task) {
			runInAction(() => this.applyTask(task));
			const me = this.clickupUserId;
			if (me && task.assignees.some((assignee) => String(assignee.id) === me)) KonvoTasks.upsertTask(task);
		}
		void this.loadHierarchy(true);
		return task;
	}

	/** Status change with an optimistic update; rolls back if ClickUp refuses. */
	async setStatus(task: KonvoTask, status: KonvoTaskListStatus): Promise<void> {
		if (!task.id || !status.status) return;
		const before = task;
		this.applyTask({...task, status: {status: status.status, color: status.color, type: status.type}});
		try {
			await KonvoTasks.updateTask(task.id, {status: status.status});
		} catch (error) {
			this.applyTask(before);
			throw error;
		}
	}

	async complete(task: KonvoTask): Promise<void> {
		if (!task.id) return;
		const listId = task.list.id;
		if (listId) await this.loadListMeta(listId);
		const statuses = this.statusesFor(listId);
		const closed =
			statuses.find((status) => status.type === 'closed') ??
			statuses.find((status) => status.type === 'done') ??
			statuses[statuses.length - 1];
		if (closed) await this.setStatus(task, closed);
		else await KonvoTasks.completeTask(task.id);
	}

	// ── Live updates ───────────────────────────────────────────────────────

	/** Gateway: something changed in ClickUp. Batch, then refetch what's on screen. */
	handleTasksChanged(event: TasksChangedEvent): void {
		const pending = this.pendingChange ?? {task_ids: [], list_ids: [], space_ids: [], hierarchy: false};
		pending.task_ids = [...(pending.task_ids ?? []), ...(event.task_ids ?? [])];
		pending.list_ids = [...(pending.list_ids ?? []), ...(event.list_ids ?? [])];
		pending.space_ids = [...(pending.space_ids ?? []), ...(event.space_ids ?? [])];
		pending.hierarchy = pending.hierarchy || event.hierarchy === true;
		this.pendingChange = pending;
		if (this.changeTimer) return;
		this.changeTimer = setTimeout(() => {
			this.changeTimer = null;
			const change = this.pendingChange;
			this.pendingChange = null;
			if (change) void this.refreshFor(change);
		}, 800);
	}

	private async refreshFor(change: TasksChangedEvent): Promise<void> {
		const lists = new Set(change.list_ids ?? []);
		const spaces = new Set(change.space_ids ?? []);
		const tasks = new Set(change.task_ids ?? []);
		void this.loadHierarchy(true);
		for (const view of [...this.views.values()]) {
			const {location} = view;
			const touches =
				location.kind === 'mine' ||
				location.kind === 'today' ||
				location.kind === 'all' ||
				location.kind === 'home' ||
				(location.kind === 'list' && lists.has(location.id ?? '')) ||
				(location.kind === 'space' && spaces.has(location.id ?? '')) ||
				(location.kind === 'folder' &&
					this.allLists.some((entry) => entry.folder?.id === location.id && lists.has(entry.list.id))) ||
				view.tasks.some((task) => tasks.has(task.id ?? ''));
			if (touches) void this.loadView(location, {includeClosed: view.includeClosed, mine: view.mine}, true);
		}
		for (const spaceId of this.overviews.keys()) {
			if (spaces.has(spaceId)) void this.loadOverview(spaceId);
		}
		for (const listId of lists) if (this.listMeta.has(listId) && change.hierarchy) void this.loadListMeta(listId, true);
		if (tasks.size > 0) void KonvoTasks.refresh();
	}

	/**
	 * Refetch everything the server shapes per person: after "Counts as done in Konvo"
	 * changes, every count, view, list's statuses, overview and open task card reads differently.
	 */
	refreshAll(): void {
		void this.loadHierarchy(true);
		for (const view of [...this.views.values()]) {
			void this.loadView(view.location, {includeClosed: view.includeClosed, mine: view.mine}, true);
		}
		for (const listId of [...this.listMeta.keys()]) void this.loadListMeta(listId, true);
		for (const spaceId of [...this.overviews.keys()]) void this.loadOverview(spaceId);
		void KonvoTasks.refresh();
		for (const taskId of [...KonvoTasks.details.keys()]) void KonvoTasks.loadDetail(taskId, true);
	}

	/** True while a view for this exact location is being shown (used to skip redundant loads). */
	isShowing(location: WorkspaceLocation): boolean {
		return [...this.views.values()].some((view) => sameLocation(view.location, location) && view.loading);
	}
}

export default new KonvoWorkspaceStore();
